export const PUBLIC_SITE_PAGE_ORDER = ['branding', 'home', 'features', 'pricing', 'demo', 'contact'];

export const PUBLIC_SITE_PREVIEW_PATHS = {
  branding: '/',
  home: '/',
  features: '/features',
  pricing: '/pricing',
  demo: '/demo',
  contact: '/contact',
  test: '/test',
};

export const PUBLIC_SITE_DEFAULTS = {
  branding: {
    logoImageUrl: '',
    logoAlt: 'Servsolo logo',
    logoWidthDesktop: 280,
    logoWidthMobile: 210,
    backgroundColor: '',
    wrapperColor: '',
    wrapperEnabled: true,
    // One ordered list — display order is array order, hidden items stay in the list (so
    // they can be un-hidden) but get a `hidden: true` flag instead of a separate
    // hiddenPageKeys set living apart from the items themselves.
    navItems: [
      { label: 'Home', to: '/home' },
      { label: 'Features', to: '/features' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Demo', to: '/demo' },
      { label: 'Contact', to: '/contact' },
    ],
  },
  home: {
    customSections: [
      {
        id: 'home-hero',
        background: '#eff6ff',
        borderStyle: 'none',
        children: [
          { type: 'text', html: '<strong style="font-size:2.4em;display:block;font-weight:800;line-height:1.15">Run your whole venue<br>from one place</strong>', align: 'center' },
          { type: 'gap', size: 'small' },
          { type: 'text', html: '<span style="font-size:1.1em;color:#475569">EFTPOS payments, online ordering, and a no-code website — all in one dashboard. No technical skills needed.</span>', align: 'center' },
          { type: 'gap', size: 'small' },
          { type: 'group', groupType: 'button', columns: 1, children: [
            { type: 'button', label: 'Get Started Free', to: '/login' },
          ]},
        ],
      },
      {
        id: 'home-features',
        background: '#ffffff',
        borderStyle: 'none',
        children: [
          { type: 'text', html: '<strong style="font-size:1.8em;display:block;font-weight:700">Everything you need, nothing you don\'t</strong>', align: 'center' },
          { type: 'gap', size: 'small' },
          { type: 'group', groupType: 'solution', columns: 2, children: [
            { type: 'solution', image: '', alt: '', title: 'EFTPOS &amp; Card Payments', text: 'Accept tap, swipe, and card payments in-store. Daily settlement straight to your account with a simple 1% commission — no monthly hardware fees.' },
            { type: 'solution', image: '', alt: '', title: 'Online Ordering &amp; Kiosk', text: 'Let customers order from their phone or a self-serve kiosk. Orders go straight to your kitchen — no third-party commission.' },
            { type: 'solution', image: '', alt: '', title: 'No-Code Website Builder', text: 'Build your own branded storefront with drag-and-drop blocks. Go live in minutes with your menu, contact details, and a direct order link.' },
            { type: 'solution', image: '', alt: '', title: 'Products, Modifiers &amp; Staff', text: 'Manage your full menu with modifiers and variants. Set opening hours, control online and kiosk availability, and manage your team — all from one portal.' },
          ]},
        ],
      },
      {
        id: 'home-pricing',
        background: '#f8fafc',
        borderStyle: 'none',
        children: [
          { type: 'text', html: '<strong style="font-size:1.8em;display:block;font-weight:700">Simple pricing</strong>', align: 'center' },
          { type: 'text', html: '<span style="color:#475569">One plan, everything included. Pay monthly or save with annual billing.</span>', align: 'center' },
          { type: 'gap', size: 'small' },
          { type: 'group', groupType: 'stat', columns: 2, children: [
            { type: 'stat', value: '$59<span style="font-size:0.6em">/mo</span>', label: 'Monthly — billed monthly' },
            { type: 'stat', value: '$49<span style="font-size:0.6em">/mo</span>', label: 'Annual — save 17%' },
          ]},
          { type: 'gap', size: 'small' },
          { type: 'group', groupType: 'button', columns: 1, children: [
            { type: 'button', label: 'View Full Pricing', to: '/pricing' },
          ]},
        ],
      },
      {
        id: 'home-cta',
        background: '#1e3a8a',
        borderStyle: 'none',
        children: [
          { type: 'text', html: '<strong style="font-size:1.8em;display:block;font-weight:700;color:#ffffff">Ready to take control of your venue?</strong>', align: 'center' },
          { type: 'gap', size: 'small' },
          { type: 'group', groupType: 'button', columns: 1, children: [
            { type: 'button', label: 'Get Started Free', to: '/login' },
          ]},
        ],
      },
    ],
  },
  features: {
    customSections: [
      {
        id: 'feat-header',
        background: '#eff6ff',
        borderStyle: 'none',
        children: [
          { type: 'text', html: '<strong style="font-size:2.2em;display:block;font-weight:800">Features</strong>', align: 'center' },
          { type: 'gap', size: 'small' },
          { type: 'text', html: '<span style="font-size:1.1em;color:#475569">One platform to run payments, orders, your website, and your team.</span>', align: 'center' },
        ],
      },
      {
        id: 'feat-detail',
        background: '#ffffff',
        borderStyle: 'none',
        children: [
          { type: 'text', html: '<strong style="font-size:1.5em;display:block;font-weight:700">EFTPOS &amp; Card Payments</strong>' },
          { type: 'text', html: 'Accept tap, swipe, and chip payments in-store via integrated EFTPOS terminals. Settlement happens daily — proceeds land in your account automatically. We charge a simple 1% commission on transactions, with no monthly terminal hire or hidden fees.' },
          { type: 'divider' },
          { type: 'text', html: '<strong style="font-size:1.5em;display:block;font-weight:700">Online Ordering &amp; Self-Serve Kiosk</strong>' },
          { type: 'text', html: 'Customers can browse your menu and place orders from any device — or use a tablet kiosk in-store. Orders go straight to your kitchen. No third-party ordering platform, no commission per order, no sharing of your customer data.' },
          { type: 'divider' },
          { type: 'text', html: '<strong style="font-size:1.5em;display:block;font-weight:700">No-Code Website Builder</strong>' },
          { type: 'text', html: 'Build a branded storefront with a drag-and-drop editor. Add sections, images, feature cards, and call-to-action buttons. Publish changes live in seconds — no developer or hosting costs required.' },
          { type: 'divider' },
          { type: 'text', html: '<strong style="font-size:1.5em;display:block;font-weight:700">Products, Modifiers &amp; Staff Controls</strong>' },
          { type: 'text', html: 'Build your full menu with products and modifier groups — sizes, extras, dietary options. Control what\'s available for kiosk vs. online orders. Set your opening hours and toggle availability on or off at any time.' },
        ],
      },
      {
        id: 'feat-cta',
        background: '#eff6ff',
        borderStyle: 'none',
        children: [
          { type: 'text', html: '<strong style="font-size:1.6em;display:block;font-weight:700">Ready to get started?</strong>', align: 'center' },
          { type: 'gap', size: 'small' },
          { type: 'group', groupType: 'button', columns: 1, children: [
            { type: 'button', label: 'Get Started Free', to: '/login' },
          ]},
        ],
      },
    ],
  },
  pricing: {
    customSections: [
      {
        id: 'price-header',
        background: '#eff6ff',
        borderStyle: 'none',
        children: [
          { type: 'text', html: '<strong style="font-size:2.2em;display:block;font-weight:800">Simple, transparent pricing</strong>', align: 'center' },
          { type: 'gap', size: 'small' },
          { type: 'text', html: '<span style="font-size:1.1em;color:#475569">No setup fees. No long-term contracts. Cancel any time.</span>', align: 'center' },
        ],
      },
      {
        id: 'price-plans',
        background: '#ffffff',
        borderStyle: 'none',
        children: [
          { type: 'group', groupType: 'stat', columns: 2, children: [
            { type: 'stat', value: '$59<span style="font-size:0.6em">/mo</span>', label: 'Monthly — billed monthly, cancel any time' },
            { type: 'stat', value: '$49<span style="font-size:0.6em">/mo</span>', label: 'Annual — billed $588/year, save 17%' },
          ]},
          { type: 'gap', size: 'small' },
          { type: 'text', html: '<strong>Everything included in both plans:</strong>', align: 'center' },
          { type: 'gap', size: 'small' },
          { type: 'group', groupType: 'chip', columns: 2, children: [
            { type: 'chip', html: '<b>EFTPOS &amp; card payments</b>' },
            { type: 'chip', html: '<b>Online ordering</b>' },
            { type: 'chip', html: '<b>Self-serve kiosk</b>' },
            { type: 'chip', html: '<b>No-code website builder</b>' },
            { type: 'chip', html: '<b>Products &amp; modifiers</b>' },
            { type: 'chip', html: '<b>Staff &amp; hours controls</b>' },
            { type: 'chip', html: '<b>Daily EFTPOS settlement</b>' },
            { type: 'chip', html: '<b>Unlimited orders</b>' },
          ]},
          { type: 'gap', size: 'small' },
          { type: 'text', html: '<span style="font-size:0.95em;color:#64748b">Plus 1% commission on in-store EFTPOS transactions. No commission on online or kiosk orders.</span>', align: 'center' },
          { type: 'gap', size: 'small' },
          { type: 'group', groupType: 'button', columns: 1, children: [
            { type: 'button', label: 'Get Started', to: '/login' },
          ]},
        ],
      },
    ],
  },
  demo: {
    customSections: [],
  },
  contact: {
    customSections: [
      {
        id: 'contact-header',
        background: '#eff6ff',
        borderStyle: 'none',
        children: [
          { type: 'text', html: '<strong style="font-size:2.2em;display:block;font-weight:800">Contact</strong>', align: 'center' },
          { type: 'gap', size: 'small' },
          { type: 'text', html: '<span style="font-size:1.1em;color:#475569">Have a question? We\'d love to hear from you.</span>', align: 'center' },
        ],
      },
      {
        id: 'contact-body',
        background: '#ffffff',
        borderStyle: 'none',
        children: [
          { type: 'text', html: '<strong>Email us</strong>' },
          { type: 'text', html: '<span style="font-size:1.1em">hello@servsolo.com</span>' },
          { type: 'gap', size: 'small' },
          { type: 'text', html: '<span style="color:#475569">We typically respond within one business day.</span>' },
          { type: 'gap', size: 'small' },
          { type: 'group', groupType: 'button', columns: 1, children: [
            { type: 'button', label: 'Get Started Free', to: '/login' },
          ]},
        ],
      },
    ],
  },
  // Deliberately not in PUBLIC_SITE_PAGE_ORDER — a blank sandbox for trying out the
  // section/container/item editor without touching the live Home page. No hero, no fixed
  // sections, nothing but whatever custom sections get added, and no nav item of its own.
  test: {
    customSections: [],
  },
};

const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

export const deepMergePublicSiteContent = (base, override) => {
  if (Array.isArray(base) || Array.isArray(override)) {
    return Array.isArray(override) ? override : base;
  }

  if (!isPlainObject(base) || !isPlainObject(override)) {
    return override ?? base;
  }

  const next = { ...base };
  Object.entries(override).forEach(([key, value]) => {
    next[key] = isPlainObject(value) && isPlainObject(base[key])
      ? deepMergePublicSiteContent(base[key], value)
      : value;
  });
  return next;
};

export const clonePublicSiteDefaults = () => JSON.parse(JSON.stringify(PUBLIC_SITE_DEFAULTS));

export const buildPublicSiteContentMap = (rows = []) => {
  const defaults = clonePublicSiteDefaults();
  rows.forEach((row) => {
    if (!row?.page_slug || !(row.page_slug in defaults)) return;
    defaults[row.page_slug] = deepMergePublicSiteContent(defaults[row.page_slug], row.content || {});
  });
  return defaults;
};

// One-time read-path migration from the old { navItems, hiddenPageKeys } shape (a separate
// set of hidden page keys living apart from the nav list) to one ordered navItems array where
// each item carries its own `hidden` flag. Rows saved before this change still have
// hiddenPageKeys and no per-item `hidden` — this folds that into the new shape on read, and a
// subsequent Save persists the new shape, so this fallback only ever matters once per row.
export const normalizeBrandingNavItems = (branding) => {
  const items = Array.isArray(branding?.navItems) ? branding.navItems : PUBLIC_SITE_DEFAULTS.branding.navItems;
  const legacyHidden = new Set(Array.isArray(branding?.hiddenPageKeys) ? branding.hiddenPageKeys : []);
  return items.map((item) => ({
    ...item,
    hidden: typeof item?.hidden === 'boolean' ? item.hidden : legacyHidden.has(String(item?.to || '').replace(/^\//, '').trim()),
  }));
};